import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ShoppingCart, Search, Eye, X as IconX, Package } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { WarehouseScopeBar } from '../../components/WarehouseScopeBar';
import {
  warehousesOf, whPurchaseOrdersOf, findCatalogProduct, variantLabelOf, catalogProductsOf
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const formatDateTime = (dateString) => {
  if (!dateString) return '—';
  try {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return dateString;
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    }).format(d);
  // eslint-disable-next-line no-unused-vars
  } catch (_e) {
    return dateString;
  }
};

export const OrgWhPurchasesTab = ({ org }) => {
  const { version: dataVersion } = useSharedStore();
  const [scope, setScope] = useState('all');
  const [search, setSearch] = useState('');
  const [viewReceipt, setViewReceipt] = useState(null);
  const accent = org.theme.primary;
  const products = useMemo(() => catalogProductsOf(org.id), [org.id, dataVersion]);

  const rows = useMemo(() => {
    const whs = scope === 'all' ? warehousesOf(org.id) : warehousesOf(org.id).filter((w) => w.id === scope);
    const out = [];
    whs.forEach((w) => {
      whPurchaseOrdersOf(w.id).forEach((po) => {
        out.push({
          ...po,
          warehouseName: w.name,
        });
      });
    });
    out.sort((a, b) => ((a.date || '') < (b.date || '') ? 1 : (a.date || '') > (b.date || '') ? -1 : 0));
    if (!search.trim()) return out;
    const q = search.toLowerCase();
    return out.filter((r) =>
      (r.supplier || '').toLowerCase().includes(q)
      || (r.warehouseName || '').toLowerCase().includes(q));
  }, [scope, search, org.id, dataVersion]);

  const lineAmt = (ln) => {
    if (ln.amount !== undefined) return Number(ln.amount);
    let bp = Number(ln.originalPrice) || 0;
    let bq = Number(ln.billedQty) || 0;
    let base = bp * bq;
    let discAmt = Number(ln.discountAmount) || 0;
    let discPct = Number(ln.discountPercentage) || 0;
    let gross = base;
    if (ln.discountType === 'percentage' && discPct > 0) {
      gross = base - (base * (discPct / 100));
    } else if (discAmt > 0) {
      gross = base - discAmt;
    }
    return gross;
  };

  const calculateCost = (origP, dAmt, dPct, dType, bQty) => {
    let p = Number(origP) || 0;
    if (dType === 'percentage' && Number(dPct) > 0) {
      p = p - (p * (Number(dPct) / 100));
    } else if (Number(dAmt) > 0 && Number(bQty) > 0) {
      p = p - (Number(dAmt) / Number(bQty));
    }
    return Math.max(0, p);
  };

  return (
    <div className="flex flex-col gap-4">
      <WarehouseScopeBar org={org} value={scope} onChange={setScope} accent={accent} />

      <motion.section
        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white border border-[var(--theme-tertiary)] rounded-3xl p-6 shadow-sm flex flex-col gap-4"
      >
        <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-base font-extrabold tracking-tight text-[#0d0d0d] inline-flex items-center gap-2">
              <ShoppingCart size={16} style={{ color: accent }} aria-hidden="true" />
              Warehouse purchases
            </h3>
            <p className="text-[11px] font-medium text-slate-500 mt-1">All incoming stock orders across your warehouses (read-only).</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <input
                type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search supplier, warehouse…"
                className="w-56 bg-[var(--theme-secondary)] border border-[var(--theme-tertiary)] focus:bg-white focus:border-[var(--theme-primary)] rounded-lg pl-9 pr-4 py-2 text-xs font-medium text-[#0d0d0d] outline-none transition-colors"
              />
            </div>
          </div>
        </header>

        {rows.length === 0 ? (
          <div className="py-12 border border-dashed border-[var(--theme-tertiary)] rounded-lg text-center">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-slate-400">No purchase orders.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 border-b border-[var(--theme-tertiary)]">
                  <th className="py-2 pr-3">Warehouse</th>
                  <th className="py-2 pr-3">Supplier</th>
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Time</th>
                  <th className="py-2 pr-3 text-right">Total Amount</th>
                  <th className="py-2 px-3 text-center">Status</th>
                  <th className="py-2 pr-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((po) => {
                  let dPart = '—';
                  let tPart = '—';
                  if (po.date) {
                    const dt = formatDateTime(po.date);
                    if (dt.includes(', ')) {
                      [dPart, tPart] = dt.split(', ');
                    } else {
                      dPart = dt;
                    }
                  }
                  return (
                    <tr key={po.id} className="border-b border-[var(--theme-tertiary)] last:border-b-0">
                      <td className="py-2 pr-3 font-bold text-[#0d0d0d]">{po.warehouseName}</td>
                      <td className="py-2 pr-3 text-slate-700 font-bold">{po.supplier || '—'}</td>
                      <td className="py-2 pr-3 text-slate-600 font-medium">{dPart}</td>
                      <td className="py-2 pr-3 text-slate-500">{tPart}</td>
                      <td className="py-2 pr-3 text-slate-700 text-right font-bold">₹{(po.amount || 0).toFixed(2)}</td>
                      <td className="py-2 px-3 text-center text-slate-600 font-bold capitalize">{po.status}</td>

                      <td className="py-2 pr-3 text-right">
                        <div className="inline-flex items-center justify-end">
                          <button
                            type="button"
                            onClick={() => setViewReceipt(po)}
                            aria-label="View order details"
                            className="p-1.5 rounded-full text-slate-500 hover:text-[var(--theme-primary)] hover:bg-[var(--theme-primary-soft)] transition-colors"
                          >
                            <Eye size={12} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </motion.section>

      <AnimatePresence>
        {viewReceipt && (
          <motion.section
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-7xl flex flex-col max-h-[90vh] overflow-hidden"
            >
              <header className="px-6 py-4 flex items-center justify-between border-b border-slate-200">
                <div>
                  <h3 className="text-lg font-extrabold text-[#0d0d0d] flex items-center gap-2">
                    <Package size={20} style={{ color: org.theme.primary }} /> Purchase Order Details
                    <span
                      className="ml-2 inline-flex items-center px-2 py-0.5 rounded-lg text-[10px] font-bold uppercase tracking-[0.12em]"
                      style={{
                        background: viewReceipt.status === 'Received' ? 'rgba(16,185,129,0.12)' : 'rgba(245,158,11,0.12)',
                        color: viewReceipt.status === 'Received' ? '#0a7d3c' : '#b45309',
                      }}
                    >
                      {viewReceipt.status}
                    </span>
                  </h3>
                  <p className="text-xs text-slate-500 font-medium mt-1">
                    {viewReceipt.warehouseName} &bull; {viewReceipt.supplier || 'Unknown Supplier'} &bull; {viewReceipt.date ? formatDateTime(viewReceipt.date) : 'No Date'}
                  </p>
                </div>
                <button type="button" onClick={() => setViewReceipt(null)} className="p-2 rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors">
                  <IconX size={20} />
                </button>
              </header>

              <div className="flex-1 overflow-auto p-6 bg-slate-50">
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-[11px] bg-white">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-left text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">
                        <th className="py-3 px-4">Product</th>
                        <th className="py-3 px-4">Variant</th>
                        <th className="py-3 px-4">Details</th>
                        <th className="py-3 px-4 text-right">Price</th>
                        <th className="py-3 px-4 text-center">Flat Discount (₹)</th>
                        <th className="py-3 px-4 text-center">Discount Percent (%)</th>
                        <th className="py-3 px-4 text-center">Free Items</th>
                        <th className="py-3 px-4 text-center">Billed Items</th>
                        <th className="py-3 px-4 text-right">Cost</th>
                        <th className="py-3 px-4 text-right">Gross Amount</th>
                        <th className="py-3 px-4 text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewReceipt.lines || [viewReceipt]).map((ln, i) => {
                        const product = products.find((p) => p.id === ln.productId) || null;
                        const cost = calculateCost(ln.originalPrice, ln.discountAmount, ln.discountPercentage, ln.discountType, ln.billedQty);
                        const amt = lineAmt(ln);
                        const grossAmt = Number(ln.originalPrice || 0) * Number(ln.billedQty || 0);

                        return (
                          <tr key={i} className="border-b border-slate-100 last:border-b-0">
                            <td className="py-3 px-4 font-bold text-[#0d0d0d]">{product?.name || ln.productId}</td>
                            <td className="py-3 px-4 text-slate-600">{variantLabelOf(product, ln.variantId)}</td>
                            <td className="py-3 px-4 text-slate-600 capitalize">{ln.purchaseType || 'item'}{ln.purchaseType === 'unit' ? ' (Unit)' : ''}</td>
                            <td className="py-3 px-4 text-slate-700 text-right">₹{(Number(ln.originalPrice) || 0).toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.discountAmount) > 0 ? `₹${ln.discountAmount}` : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.discountPercentage) > 0 ? `${ln.discountPercentage}%` : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{Number(ln.bonusQty) > 0 ? ln.bonusQty : '—'}</td>
                            <td className="py-3 px-4 text-slate-700 text-center">{ln.billedQty || 0}</td>
                            <td className="py-3 px-4 text-slate-700 text-right">₹{cost.toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-right font-bold">₹{grossAmt.toFixed(2)}</td>
                            <td className="py-3 px-4 text-slate-700 text-right font-bold" style={{ color: org.theme.primary }}>₹{amt.toFixed(2)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <footer className="px-6 py-4 border-t border-[var(--theme-tertiary)] flex items-center justify-between shrink-0 bg-white">
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">Order Total</p>
                  <p className="text-2xl font-extrabold" style={{ color: org.theme.primary }}>
                    ₹{(viewReceipt.lines || [viewReceipt]).reduce((s, ln) => s + lineAmt(ln), 0).toFixed(2)}
                  </p>
                </div>
                <button type="button" onClick={() => setViewReceipt(null)} className="px-6 py-2.5 rounded-lg font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors">
                  Close
                </button>
              </footer>
            </motion.div>
          </motion.section>
        )}
      </AnimatePresence>
    </div>
  );
};

OrgWhPurchasesTab.propTypes = {
  org: PropTypes.object.isRequired,
};
